<template>
  <wizard-form v-bind="ouiaProps" :hidden="hidden" :title="title">
    <pf-flex v-if="wizard.askOs">
      <pf-flex-item>
        <pf-title h="3">{{ $t('Sistema Operativo') }}:</pf-title>
      </pf-flex-item>
      <pf-flex-item>
        <wizard-question-os v-model="wizard.os" />
      </pf-flex-item>
    </pf-flex>

    <pf-grid :sm="split && !wizard.modal ? 6 : 12" gutter>
      <pf-grid-item>
        <wizard-configuratore-cloud
          v-if="wizard.path === 'cloud'"
          v-model:cpu-qtn="wizard.cpuCloudQt"
          v-model:ram-qtn="wizard.ramCloudQt"
          v-model:storage-qtn="wizard.storageCloudQt"
        />

        <wizard-question v-else-if="wizard.path === 'storage'">
          <wizard-question-prodotti
            v-model="wizard.sceltaProdottoInfrastruttura"
            v-model:qt="wizard.storageQt"
            :ask-qt="wizard.askStorageQt"
            :prodotti="wizard.opzioniProdottiInfrastruttura"
            name="sceltaProdottoInfrastruttura"
            show-feature
            variant="list"
            required
          />

          <pf-title h="3" class="pf-v6-u-mt-md">{{ $t('Backup') }}</pf-title>
          <pf-radio
            name="sceltaProdottoInfrastruttura"
            required
            :value="wizard.codiceProdottoBackup"
            :checked="wizard.sceltaProdottoInfrastruttura === wizard.codiceProdottoBackup"
            @change="wizard.sceltaProdottoInfrastruttura = wizard.codiceProdottoBackup"
          >
            <template #label>
              {{ wizard.getProdotto(wizard.codiceProdottoBackup).nome }}

              <wizard-price-tag :product="wizard.getProdotto(wizard.codiceProdottoBackup)" />
            </template>
            <template #description>
              <product-details :product="wizard.getProdotto(wizard.codiceProdottoBackup)" style="max-width: 80ch" />
              {{
                $t('Lo spazio occupato dai backup verrà fatturato a consumo, al costo di {0} /GB /Mese', [
                  $currency(wizard.getProdotto(wizard.codiceProdottoSpazioBackup).prezzo_scontato),
                  wizard.customer?.currency,
                ])
              }}
            </template>
          </pf-radio>
        </wizard-question>

        <wizard-question v-else>
          <wizard-question-prodotti
            v-model="wizard.sceltaProdottoInfrastruttura"
            v-model:qt="wizard.storageQt"
            :ask-qt="wizard.askStorageQt"
            :prodotti="wizard.opzioniProdottiInfrastruttura"
            name="sceltaProdottoInfrastruttura"
            show-feature
            variant="table"
            required
          />
        </wizard-question>
      </pf-grid-item>

      <pf-grid-item sm="6" v-if="split">
        <wizard-question v-if="wizard.askNomeServer" :title="$t('Nome del server')" style="max-width: 400px">
          <wizard-question-nomeserver v-model="wizard.nomeServer" />
        </wizard-question>

        <template v-if="wizard.path === 'host'">
          <wizard-question :title="$t('Vuoi attivare della RAM aggiuntiva?')">
            <wizard-question-prodotto
              v-model="wizard.addOn32Ram"
              v-model:qt="wizard.addOn32RamQt"
              :product="wizard.getProdotto(wizard.codiciProdottiAddon.addOn32Ram)"
              ask-qt
              show-prezzo
              um="GB"
              :qt-step="32"
            />
          </wizard-question>

          <wizard-question :title="$t('Vuoi attivare uno storage aggiuntivo?')">
            <wizard-question-prodotto
              v-model="wizard.addonStorage"
              v-model:qt="wizard.qtAddonStorage"
              :product="wizard.getProdotto(wizard.codiciProdottiAddon.addOnStorageHost)"
              show-prezzo
              ask-qt
              um="GB"
              :qt-step="250"
            />
          </wizard-question>

          <wizard-question :title="$t('Vuoi attivare anche indirizzi IP dedicati aggiuntivi?')">
            <wizard-question-prodotto
              v-model="wizard.addOn8Ip"
              v-model:qt="wizard.addOn8IpQt"
              :product="wizard.getProdotto(wizard.codiciProdottiAddon.addOn8Ip)"
              ask-qt
              show-prezzo
              um="IP"
              :qt-step="8"
            />
          </wizard-question>

          <wizard-question :title="$t('Vuoi attivare anche le licenze di Windows?')">
            <wizard-question-prodotto
              v-model="wizard.addHostWin"
              :product="wizard.getProdotto(wizard.codiciProdottiAddon.winServer)"
              :qt="Math.round(Number(wizard.getProdotto(wizard.sceltaProdottoInfrastruttura).core) / 2)"
              ask-qt
              fixed-qt
              show-prezzo
            />
          </wizard-question>
        </template>

        <wizard-question v-if="wizard.askFirewall" :title="$t('Vuoi attivare anche un firewall dedicato?')">
          <wizard-question-prodotto
            v-model="wizard.addOnFirewall"
            :product="wizard.firewall"
            show-prezzo
            :off-text="$t('No, utilizzo quello a bordo del server')"
          />
        </wizard-question>

        <wizard-question v-if="wizard.askAddonStorage" :title="$t('Vuoi attivare uno storage aggiuntivo?')">
          <pf-radio
            name="addon_storage"
            value=""
            :label="$t('No')"
            :checked="wizard.codiceStorageCloud === ''"
            @change="wizard.codiceStorageCloud = ''"
            class="wizard-radio"
          />
          <wizard-question-prodotti
            v-model="wizard.codiceStorageCloud"
            v-model:qt="wizard.qtAddonStorage"
            :prodotti="wizard.codiciProdottiAddon.storageCloud"
            name="addon_storage"
            ask-qt
            um="TB"
            variant="list"
            required
          />
        </wizard-question>
      </pf-grid-item>
    </pf-grid>
  </wizard-form>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { $t as _t } from '@/i18n';
import { useOUIAProps, type OUIAProps } from '@common/ouia';
import { useWizardStep } from '@/wizard';

import WizardQuestion from '../questions/wizard-question.vue';
import WizardQuestionOs from '../questions/wizard-question-os.vue';
import wizardQuestionProdotto from '../questions/wizard-question-prodotto.vue';
import WizardQuestionProdotti from '../questions/wizard-question-prodotti.vue';
import WizardConfiguratoreCloud from '../questions/wizard-configuratore-cloud.vue';
import WizardQuestionNomeserver from '../questions/wizard-question-nomeserver.vue';
import WizardForm from '../wizard-form.vue';
import WizardPriceTag from '../wizard-price-tag.vue';
import ProductDetails from '@/components/product-details.vue';

defineOptions({
  name: 'WizardStepProdottiInfrastruttura',
});

const props = defineProps<OUIAProps>();
const ouiaProps = useOUIAProps(props);

const { wizard, hidden, isEnabled } = useWizardStep({
  isEnabled: wizard => wizard.askProdotti,
});

defineExpose({
  isEnabled,
});

const title = computed(() => (wizard.path === 'cloud' ? _t('Configura il tuo server') : _t('Scelta del prodotto')));
const split = computed(() => wizard.askNomeServer || wizard.path === 'host' || wizard.askFirewall);
</script>
